import React from 'react';

import { isPresent, valueOrPlaceholder } from 'helpers/string_helpers';

import { toggleSelectProperty } from '../store/actions/select_list';
import { goToPropertyShow } from '../store/actions/properties';
import { stringToUrl } from 'helpers/formatters/string_formats';

import CallContactLink from 'components/click_to_call/call_contact_link';
import SelectableRow from 'components/selectable_record_table/selectable_row';
import StatusLabel from 'components/status_label';

import { useSelector, useDispatch } from '../store/hooks';

import type { Property } from '../types';

interface PropertyRowProps {
  property: Property;
  selected: boolean;
}

export default function PropertyRow({ property, selected }: PropertyRowProps) {
  const edgeCrm = useSelector(state => state.features.edgeCrm);
  const currentUser = useSelector(state => state.scope.currentUser);
  const currentColumns = useSelector(state => state.session.currentColumns);
  const customFieldSchemas = useSelector(state => state.static.customFields);

  const dispatch = useDispatch();
  const dispatchedGoToPropertyShow = (e, property) => dispatch(goToPropertyShow(e, property));
  const onSelect = property => dispatch(toggleSelectProperty(property));

  const handleSelect = () => {
    if (onSelect != null) {
      onSelect(property);
    }
  };

  function renderPropertyCells() {
    const propertyCells: (React.JSX.Element | undefined)[] = [];
    propertyCells.push(
      <td key={'name'}>
        <a
          className="contact-link"
          onClick={e => dispatchedGoToPropertyShow(e, property)}
        >
          <div className="d-flex flex-column">
            <strong>{property.name}</strong>
            <span className="d-lg-none d-inline text-muted">
              {property.summary}
            </span>
          </div>
        </a>
      </td>
    );
    propertyCells.push(renderSearchResults());

    currentColumns.forEach((column) => {
      if (column === 'ownerPhone') {
        propertyCells.push(
          <td className="d-none d-lg-table-cell" key={column}>
            <CallContactLink
              phone={property.ownerPhone}
              phoneNormalized={property.ownerPhoneNormalized}
              targetName={property.ownerName}
              user={currentUser}
            />
          </td>
        );
      } else if (column === 'ownerName' && isPresent(property[column])) {
        propertyCells.push(
          <td className="d-none d-lg-table-cell" key={column}>
            <a className="contact-link" href={property.ownerUrl}>
              {property[column]}
            </a>
          </td>
        );
      } else if (
        column === 'projectDealStatus' &&
        isPresent(property[column])
      ) {
        propertyCells.push(
          <td className="d-none d-lg-table-cell" key={column}>
            <StatusLabel.Pill status={property[column]} />
          </td>
        );
      } else if (column.startsWith('c_')) {
        const customField = property.customFieldValues?.find(
          field => field.name === column
        );
        const customFieldSchema = customFieldSchemas?.find(
          schema => schema.slug === column
        );
        let value;

        if (customFieldSchema?.url && customField?.value) {
          value = (
            <div className="d-flex flex-column" data-name={column}>
              {customField.value.split(/\s+/).map((url, i) => (
                <a href={stringToUrl(url)} key={`${column}-url-${i}`}>
                  {url}
                </a>
              ))}
            </div>
          );
        } else {
          value = (
            <span data-name={column}>
              {valueOrPlaceholder(customField?.value)}
            </span>
          );
        }

        propertyCells.push(
          <td className="d-none d-lg-table-cell" key={column}>
            {value}
          </td>
        );
      } else if (column !== 'databaseName' || !edgeCrm) {
        propertyCells.push(
          <td className="d-none d-lg-table-cell" key={column}>
            <span data-name={column}>
              {valueOrPlaceholder(property[column])}
            </span>
          </td>
        );
      }
    });

    return propertyCells;
  }

  function renderSearchResults() {
    if (property && property.searchResults) {
      const highlights = Object.entries(property.searchResults);

      if (highlights.length > 0) {
        const [label, value] = highlights[0];
        const formattedValue = (
          <span dangerouslySetInnerHTML={{ __html: value }} />
        );

        if (isPresent(label)) {
          return (
            <td data-name="search-results" key="search-results">
              <span data-name="search-result-label">{label}:</span>
              &nbsp;
              <span data-name="search-result-value">"{formattedValue}"</span>
            </td>
          );
        } else {
          return (
            <td data-name="search-results">
              <span data-name="search-result-value">{formattedValue}</span>
            </td>
          );
        }
      }
    }
  }

  return (
    <SelectableRow selected={selected} onChange={handleSelect}>
      {renderPropertyCells()}
    </SelectableRow>
  );
}
